import React, { useCallback, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/**
 * Club join QR: render, copy link, download PNG, print flyer.
 */
export default function InviteJoinQr({
  joinUrl,
  clubName,
  logoSrc,
  copied,
  onCopy,
  t,
}) {
  const canvasWrapRef = useRef(null);

  const downloadPng = useCallback(() => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas || !joinUrl) return;
    const link = document.createElement("a");
    const safe = String(clubName || "club")
      .replace(/[^\w\-]+/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 40);
    link.download = `${safe || "club"}-join-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }, [clubName, joinUrl]);

  const printFlyer = useCallback(() => {
    if (!joinUrl) return;
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
    const w = window.open("", "_blank", "noopener,noreferrer,width=480,height=720");
    if (!w) return;
    const title = clubName || "Club";
    const esc = (s) =>
      String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/"/g, "&quot;");
    w.document.write(`<!DOCTYPE html><html><head><title>${esc(title)} — Join QR</title>
<style>
  body{font-family:system-ui,sans-serif;text-align:center;padding:32px;color:#0f172a}
  img.logo{max-height:64px;max-width:160px;margin:0 auto 12px;display:block}
  h1{font-size:1.35rem;margin:0 0 8px}
  p{color:#64748b;font-size:0.95rem;margin:0 0 20px}
  img.qr{width:280px;height:280px}
  .url{margin-top:16px;font-size:0.8rem;word-break:break-all;color:#475569}
  @media print{body{padding:12px}}
</style></head><body>
${logoSrc ? `<img class="logo" src="${esc(logoSrc)}" alt="" />` : ""}
<h1>${esc(title)}</h1>
<p>Scan to join as a player</p>
${dataUrl ? `<img class="qr" src="${dataUrl}" alt="Join QR" />` : ""}
<p class="url">${esc(joinUrl)}</p>
<script>window.onload=function(){window.print();}</script>
</body></html>`);
    w.document.close();
  }, [clubName, joinUrl, logoSrc]);

  if (!joinUrl) {
    return (
      <p className="invite-error" role="status">
        {t("inviteJoinUrlMissing") || "Club join link is not available yet."}
      </p>
    );
  }

  return (
    <div className="invite-qr">
      <div className="invite-qr__frame" ref={canvasWrapRef}>
        <QRCodeCanvas
          value={joinUrl}
          size={220}
          level="M"
          includeMargin
          bgColor="#ffffff"
          fgColor="#0f172a"
        />
      </div>
      <p className="invite-share__url" title={joinUrl}>
        {joinUrl}
      </p>
      <div className="invite-qr__actions">
        <button
          type="button"
          className={`invite-btn invite-btn--ghost ${copied ? "is-copied" : ""}`}
          onClick={onCopy}
        >
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          <span>{copied ? t("copied") || "Copied" : t("copyLink") || "Copy link"}</span>
        </button>
        <button type="button" className="invite-btn invite-btn--ghost" onClick={downloadPng}>
          <DownloadIcon fontSize="small" />
          <span>{t("inviteQrDownload") || "Download PNG"}</span>
        </button>
        <button type="button" className="invite-btn invite-btn--ghost" onClick={printFlyer}>
          <PrintIcon fontSize="small" />
          <span>{t("inviteQrPrint") || "Print"}</span>
        </button>
      </div>
    </div>
  );
}
